import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

/**
 * AccountingChart
 * ---------------------------------------------------------
 * Modern dark chart UI to match the Accounting/Budget pages.
 * - Uses backend chart data from /accounting/ebay/profit
 * - Formats dates in America/New_York
 * - Shows Gross Sales vs Net Profit
 * ---------------------------------------------------------
 */

const cleanNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value) => {
  const num = cleanNumber(value);

  if (Math.abs(num) >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(1)}M`;
  }

  if (Math.abs(num) >= 1_000) {
    return `$${(num / 1_000).toFixed(1)}k`;
  }

  return `$${num.toFixed(2)}`;
};

const formatFullCurrency = (value) => {
  const num = cleanNumber(value);

  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getPeriodTitle = (period) => {
  const periodLabelMap = {
    day: "Today’s Sales vs Net Profit",
    week: "This Week’s Sales vs Net Profit",
    month: "This Month’s Sales vs Net Profit",
    year: "This Year’s Sales vs Net Profit",
    custom: "Custom Range Sales vs Net Profit",
    "": "All Time Sales vs Net Profit",
  };

  return periodLabelMap[period] || "Sales vs Net Profit";
};

const formatXAxisDate = (value, period) => {
  if (!value) return "";

  const raw = String(value).trim();

  if (period === "day" && /^\d{1,2}(:\d{2})?/.test(raw)) {
    return raw;
  }

  let date;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    date = new Date(`${raw}T00:00:00-05:00`);
  } else {
    date = new Date(raw);
  }

  if (Number.isNaN(date.getTime())) return raw;

  const opts = {
    timeZone: "America/New_York",
  };

  if (period === "week") {
    const weekday = date.toLocaleDateString("en-US", {
      weekday: "short",
      ...opts,
    });

    const monthDay = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...opts,
    });

    return `${weekday} ${monthDay}`;
  }

  if (period === "year") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      ...opts,
    });
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...opts,
  });
};

const formatTooltipLabel = (label, period) => {
  if (!label) return "";

  const raw = String(label).trim();

  if (period === "day" && /^\d{1,2}(:\d{2})?/.test(raw)) {
    return raw;
  }

  let date;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    date = new Date(`${raw}T00:00:00-05:00`);
  } else {
    date = new Date(raw);
  }

  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  });
};

function AccountingChart({ chartData = [], period = "day" }) {
  const safeChartData = Array.isArray(chartData) ? chartData : [];

  const totals = safeChartData.reduce(
    (acc, row) => {
      acc.grossSales += cleanNumber(row.gross_sales);
      acc.netProfit += cleanNumber(row.net_profit);
      return acc;
    },
    {
      grossSales: 0,
      netProfit: 0,
    }
  );

  const profitMargin =
    totals.grossSales > 0 ? (totals.netProfit / totals.grossSales) * 100 : 0;

  if (!safeChartData.length) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: 360,
          borderRadius: 22,
          background:
            "radial-gradient(circle at top right, rgba(59,130,246,0.16), transparent 34%), rgba(15, 23, 42, 0.86)",
          border: "1px solid rgba(148, 163, 184, 0.16)",
          boxShadow: "0 18px 45px rgba(0,0,0,0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
          textAlign: "center",
          padding: 24,
        }}
      >
        <div>
          <div style={{ fontSize: 38, marginBottom: 8 }}>📊</div>
          <div style={{ color: "#f8fafc", fontWeight: 900, fontSize: 20 }}>
            No chart data available
          </div>
          <div style={{ marginTop: 6, fontSize: 14 }}>
            Try another period or refresh the accounting report.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: 430,
        borderRadius: 22,
        background:
          "radial-gradient(circle at top right, rgba(59,130,246,0.18), transparent 34%), radial-gradient(circle at bottom left, rgba(34,197,94,0.10), transparent 28%), rgba(15, 23, 42, 0.86)",
        border: "1px solid rgba(148, 163, 184, 0.16)",
        boxShadow: "0 18px 45px rgba(0,0,0,0.22)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "18px 20px 8px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 14,
          alignItems: "flex-start",
        }}
      >
        <div>
          <div
            style={{
              color: "#f8fafc",
              fontWeight: 900,
              fontSize: 20,
              letterSpacing: "-0.02em",
            }}
          >
            📈 {getPeriodTitle(period)}
          </div>

          <div
            style={{
              color: "#94a3b8",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            Gross sales compared against net profit.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div
            style={{
              border: "1px solid rgba(59,130,246,0.28)",
              background: "rgba(59,130,246,0.12)",
              color: "#bfdbfe",
              borderRadius: 999,
              padding: "7px 11px",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Gross {formatFullCurrency(totals.grossSales)}
          </div>

          <div
            style={{
              border: "1px solid rgba(34,197,94,0.28)",
              background: "rgba(34,197,94,0.12)",
              color: totals.netProfit >= 0 ? "#86efac" : "#fca5a5",
              borderRadius: 999,
              padding: "7px 11px",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Net {formatFullCurrency(totals.netProfit)}
          </div>

          <div
            style={{
              border: "1px solid rgba(168,85,247,0.28)",
              background: "rgba(168,85,247,0.12)",
              color: "#ddd6fe",
              borderRadius: 999,
              padding: "7px 11px",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Margin {profitMargin.toFixed(2)}%
          </div>
        </div>
      </div>

      <div style={{ width: "100%", height: 360, padding: "8px 12px 18px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={safeChartData}
            margin={{
              top: 28,
              right: 20,
              left: 8,
              bottom: safeChartData.length > 15 ? 42 : 22,
            }}
            barGap={6}
            barCategoryGap={safeChartData.length > 20 ? "18%" : "26%"}
          >
            <defs>
              <linearGradient id="grossSalesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                <stop offset="100%" stopColor="#2563eb" stopOpacity={0.88} />
              </linearGradient>

              <linearGradient id="netProfitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#86efac" stopOpacity={1} />
                <stop offset="100%" stopColor="#16a34a" stopOpacity={0.88} />
              </linearGradient>

              <filter id="chartGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#020617" floodOpacity="0.35" />
              </filter>
            </defs>

            <CartesianGrid
              stroke="rgba(148, 163, 184, 0.16)"
              strokeDasharray="4 4"
              vertical={false}
            />

            <XAxis
              dataKey="date"
              tick={{
                fill: "#94a3b8",
                fontSize: 12,
                fontWeight: 700,
              }}
              axisLine={{
                stroke: "rgba(148, 163, 184, 0.20)",
              }}
              tickLine={{
                stroke: "rgba(148, 163, 184, 0.20)",
              }}
              interval={safeChartData.length > 10 ? "preserveStartEnd" : 0}
              angle={safeChartData.length > 15 ? -30 : 0}
              textAnchor={safeChartData.length > 15 ? "end" : "middle"}
              tickFormatter={(value) => formatXAxisDate(value, period)}
            />

            <YAxis
              tickFormatter={(value) => formatCurrency(value)}
              tick={{
                fill: "#94a3b8",
                fontSize: 12,
                fontWeight: 700,
              }}
              axisLine={{
                stroke: "rgba(148, 163, 184, 0.20)",
              }}
              tickLine={{
                stroke: "rgba(148, 163, 184, 0.20)",
              }}
            />

            <Tooltip
              cursor={{
                fill: "rgba(148, 163, 184, 0.08)",
                radius: 12,
              }}
              contentStyle={{
                background: "rgba(2, 6, 23, 0.96)",
                border: "1px solid rgba(148, 163, 184, 0.22)",
                borderRadius: 16,
                boxShadow: "0 18px 45px rgba(0,0,0,0.35)",
                color: "#e5e7eb",
              }}
              labelStyle={{
                color: "#f8fafc",
                fontWeight: 900,
                marginBottom: 6,
              }}
              itemStyle={{
                fontWeight: 800,
              }}
              formatter={(value, dataKey) => {
                const label =
                  dataKey === "gross_sales"
                    ? "Gross Sales"
                    : dataKey === "net_profit"
                    ? "Net Profit"
                    : dataKey;

                return [formatFullCurrency(value), label];
              }}
              labelFormatter={(label) => formatTooltipLabel(label, period)}
            />

            <Legend
              wrapperStyle={{
                color: "#cbd5e1",
                fontSize: 12,
                fontWeight: 800,
                paddingTop: 8,
              }}
              iconType="circle"
            />

            <Bar
              dataKey="gross_sales"
              name="Gross Sales"
              fill="url(#grossSalesGradient)"
              radius={[10, 10, 0, 0]}
              filter="url(#chartGlow)"
              maxBarSize={62}
            >
              {safeChartData.length <= 12 && (
                <LabelList
                  dataKey="gross_sales"
                  position="top"
                  formatter={(value) => formatCurrency(value)}
                  fill="#bfdbfe"
                  fontSize={11}
                  fontWeight={800}
                />
              )}
            </Bar>

            <Bar
              dataKey="net_profit"
              name="Net Profit"
              fill="url(#netProfitGradient)"
              radius={[10, 10, 0, 0]}
              filter="url(#chartGlow)"
              maxBarSize={62}
            >
              {safeChartData.length <= 12 && (
                <LabelList
                  dataKey="net_profit"
                  position="top"
                  formatter={(value) => formatCurrency(value)}
                  fill="#bbf7d0"
                  fontSize={11}
                  fontWeight={800}
                />
              )}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default AccountingChart;